import type { Media as FhirMedia } from '@medplum/fhirtypes'
import { SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { QueryTypes } from '#src/search.ts'

export class ExtendedMedia extends ExtendedResource<FhirMedia> {
  static readonly RESOURCE_TYPE = 'Media' as const

  get fhirType(): 'Media' {
    return 'Media'
  }

  get contentUrl(): string | undefined {
    return this.resource.content?.url
  }

  get contentType(): string | undefined {
    return this.resource.content?.contentType
  }

  get title(): string | undefined {
    return this.resource.content?.title
  }

  get category(): string | undefined {
    return this.resource.view?.coding?.[0]?.code
  }

  get categoryDisplay(): string | undefined {
    return this.resource.view?.coding?.[0]?.display
  }

  get createdDateTime(): string | undefined {
    return this.resource.createdDateTime
  }

  get contentHash(): string | undefined {
    return this.resource.identifier?.find(
      (id) => id.system === `${SEEN_HEALTH_SYSTEM}/media/content-hash`
    )?.value
  }

  get patientAssociation():
    | {
        patientId: string
        similarity: number
        confidence: number
        boundingBox: {
          width: number
          height: number
          left: number
          top: number
        }
      }
    | undefined {
    const tag = this.resource.meta?.tag?.find(
      (t) =>
        t.system ===
        `${SEEN_HEALTH_SYSTEM}/CodeSystem/media-patient-association`
    )

    if (!tag) {
      return undefined
    }

    const similarity = tag.extension?.find(
      (e) => e.url === `${SEEN_HEALTH_SYSTEM}/StructureDefinition/similarity`
    )?.valueDecimal

    const confidence = tag.extension?.find(
      (e) => e.url === `${SEEN_HEALTH_SYSTEM}/StructureDefinition/confidence`
    )?.valueDecimal

    const boundingBoxExtension = tag.extension?.find(
      (e) => e.url === `${SEEN_HEALTH_SYSTEM}/StructureDefinition/bounding-box`
    )

    const boundingBox = {
      width:
        boundingBoxExtension?.extension?.find((e) => e.url === 'width')
          ?.valueDecimal ?? 0,
      height:
        boundingBoxExtension?.extension?.find((e) => e.url === 'height')
          ?.valueDecimal ?? 0,
      left:
        boundingBoxExtension?.extension?.find((e) => e.url === 'left')
          ?.valueDecimal ?? 0,
      top:
        boundingBoxExtension?.extension?.find((e) => e.url === 'top')
          ?.valueDecimal ?? 0,
    }

    return {
      patientId: tag.code ?? '',
      similarity: similarity ?? 0,
      confidence: confidence ?? 0,
      boundingBox,
    }
  }

  static searchQueryForPatient(patientId: string): QueryTypes {
    return {
      _tag: `${SEEN_HEALTH_SYSTEM}/CodeSystem/media-patient-association|${patientId}`,
      _sort: '-created',
    }
  }
}
